import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import type { PropsWithChildren, ReactNode } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Expander from 'components/utils/Expander'
import styled from 'styled-components'
import { rem } from 'polished'

const NameText = styled(BodyText)`
  transition: color 300ms;
`

const UpgradeNameText = styled(BodyText)`
  transform: translateY(${rem(-16)});
  transition: transform 300ms;

  &.visible {
    transform: translateY(0);
  }
`

type Props = {
  /**
   * positive integer
   */
  packageNumber?: number
  tagLineLabel?: ReactNode
  stayPayRateTier?: App.StayPayTier
  selectedTargetUpgradePackage?: App.Package
  targetUpgradePackage?: App.Package
} & ({
  roomPackage?: App.HotelPackage
  promotion?: undefined
  rateName?: undefined
} | {
  roomPackage?: undefined
  promotion?: App.BedbankPromotion
  rateName?: undefined
} | {
  roomPackage?: undefined
  promotion?: undefined
  rateName: string
})

function AccommodationPackageTileDetails(props: PropsWithChildren<Props>) {
  const {
    children,
    packageNumber,
    tagLineLabel,
    roomPackage,
    promotion,
    rateName,
    stayPayRateTier,
    selectedTargetUpgradePackage,
    targetUpgradePackage,
  } = props

  const intl = useIntl()

  const name = roomPackage?.name ?? promotion?.name ?? rateName
  const targetUpgradeName = selectedTargetUpgradePackage?.name
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const optionLabel = packageNumber
    ? intl.formatMessage(
        {
          id: 'w9KMEY0syTUIZ2vs9rKe',
          defaultMessage: 'Option {number}',
        },
        { number: packageNumber },
      )
    : undefined

  const memberUpgradeLabel = targetUpgradeName
    ? intl.formatMessage(
        {
          id: 'pP94A9yDeWzwAUMzzNAu',
          defaultMessage: '{tierName} member upgrade',
        },
        { tierName: getLoyaltyTierDisplayName(displayTier) },
      )
    : undefined

  const nameLabel = name && stayPayRateTier
    ? intl.formatMessage(
        {
          id: 'L16RO72hXzITAlTJapUG',
          defaultMessage: '{name}, {count, plural, one {# night free} other {# nights free}}',
        },
        { name, count: stayPayRateTier.freeNights },
      )
    : name

  const optionWithUpgradeContent = memberUpgradeLabel && <FormattedMessage
    id="ZV4FM7PzeScSI0Airh5f"
    defaultMessage="{optionLabel} | <bold>{upgradeLabel}</bold>"
    
    values={{
      optionLabel,
      upgradeLabel: memberUpgradeLabel,
      bold: (chunks: React.ReactNode) => <b>{chunks}</b>,
    }}
  />

  return <div>
    <VerticalSpacer gap={4}>
      <VerticalSpacer gap={8}>
        {!!packageNumber && <Caption variant="large">
          {optionWithUpgradeContent || optionLabel}
        </Caption>}
        {!!tagLineLabel && <LabelGroup>{tagLineLabel}</LabelGroup>}
      </VerticalSpacer>
      <Expander expanded={!!selectedTargetUpgradePackage} contentTransition="delayed">
        <UpgradeNameText variant="large" weight="bold" className={selectedTargetUpgradePackage ? 'visible' : undefined}>
          {selectedTargetUpgradePackage?.name ?? targetUpgradePackage?.name}
        </UpgradeNameText>
      </Expander>
      {!!name && <NameText
        variant="large"
        weight="bold"
        colour={selectedTargetUpgradePackage ? 'light-foreground-alt' : 'normal-contrast'}
        strikeThrough={!!selectedTargetUpgradePackage}
      >
        {nameLabel}
      </NameText>}
    </VerticalSpacer>
    {children}
  </div>
}

export default AccommodationPackageTileDetails
